import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/core/utils';
import { ChevronDown, ChevronUp, ExternalLink, Undo2, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useDashboardTab } from '@/context/DashboardTabContext';
import { useAuth } from '@/context/AuthContext';
import { AI_ASSISTANT } from '@/lib/ai-chat/constants';
import { MarkdownRenderer } from './MarkdownRenderer';
import { AIQuestionsForm } from './AIQuestionsForm';
import { useTypewriter } from '@/hooks/ai-chat/useTypewriter';
import type { AIMessageProps, NavigationHint, AIToolCall, AIQuestionAnswers } from './types';

// Get initials from name or email
const getInitials = (fullName?: string | null, email?: string | null): string => {
  if (fullName) {
    return fullName
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }
  if (email) {
    return email[0].toUpperCase();
  }
  return 'U';
};

interface AIMessageComponentProps extends AIMessageProps {
  isLatest?: boolean;
  businessLogoUrl?: string | null;
  businessId?: string | null;
  onQuestionsSubmit?: (answers: AIQuestionAnswers, displayText: string) => void;
}

export const AIMessage: React.FC<AIMessageComponentProps> = ({
  message,
  onRevert,
  onConfirmTools,
  isLatest = false,
  businessLogoUrl,
  businessId,
  onQuestionsSubmit,
}) => {
  const [showToolCalls, setShowToolCalls] = useState(false);
  const [revertingToolIndex, setRevertingToolIndex] = useState<number | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [questionsSubmitted, setQuestionsSubmitted] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    setBusinessDashboardTab,
    setEmployeeDashboardTab,
    setHighlightedItem,
    setSelectedShiftDate,
    setNavigateToShiftMonth,
    setShiftToSelect
  } = useDashboardTab();
  const isUser = message.role === 'user';

  // Typewriter effect for latest AI message (only on first render)
  const { displayedText } = useTypewriter(message.content, {
    enabled: !isUser && isLatest,
  });

  const handleShowNavigation = (nav: NavigationHint) => {
    // For shifts, select for editing instead of just highlighting
    if (nav.resourceType === 'shift' && nav.resourceId) {
      setShiftToSelect(nav.resourceId);
    }
    // For other types, use highlight (employees, reviews)
    else if (nav.resourceId && (nav.resourceType === 'employee' || nav.resourceType === 'review')) {
      setHighlightedItem({ type: nav.resourceType, id: nav.resourceId });
    }

    // Navigate to date if shift - also navigate calendar to that month
    if (nav.resourceType === 'shift' && nav.date) {
      const shiftDate = new Date(nav.date);
      setSelectedShiftDate(shiftDate);
      setNavigateToShiftMonth(shiftDate); // Trigger calendar to navigate to that month
    }

    // Switch to correct dashboard and tab
    if (nav.dashboard === 'employee') {
      navigate('/employee-dashboard');
      setEmployeeDashboardTab(nav.tab);
    } else {
      navigate('/dashboard');
      setBusinessDashboardTab(nav.tab);
    }
  };

  const handleRevert = async (toolCall: AIToolCall, index: number) => {
    if (!onRevert) return;
    setRevertingToolIndex(index);
    try {
      await onRevert(toolCall);
    } finally {
      setRevertingToolIndex(null);
    }
  };

  const isCreateTool = (name: string) => name === 'create_shift' || name === 'create_employee';

  // Handle questions form submission
  const handleQuestionsSubmit = useCallback((answers: AIQuestionAnswers, displayText: string) => {
    setQuestionsSubmitted(true);
    onQuestionsSubmit?.(answers, displayText);
  }, [onQuestionsSubmit]);

  const handleConfirmTools = async () => {
    if (!onConfirmTools || !message.pendingTools) return;
    setIsConfirming(true);
    try {
      await onConfirmTools(message.pendingTools);
    } finally {
      setIsConfirming(false);
    }
  };

  const initials = getInitials(user?.user_metadata?.full_name, user?.email);
  const contentToShow = isUser ? message.content : (isLatest ? displayedText : message.content);

  return (
    <div
      className={cn(
        'flex gap-2 mb-3',
        isUser ? 'flex-row-reverse' : 'flex-row'
      )}
    >
      {/* Avatar */}
      {isUser ? (
        businessLogoUrl ? (
          <div className="flex-shrink-0 h-5 w-5 rounded-full bg-white flex items-center justify-center ring-1 ring-border">
            <img
              src={businessLogoUrl}
              alt="Business"
              className="h-3.5 w-3.5 rounded-sm"
            />
          </div>
        ) : (
          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-primary/30 to-primary/50 flex items-center justify-center ring-1 ring-border">
            <span className="text-xs font-semibold text-primary">{initials}</span>
          </div>
        )
      ) : (
        <div className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ring-1 ring-border">
          <img
            src="/media/tag-innovation-logo.png"
            alt={AI_ASSISTANT.name}
            className="h-4 w-4 rounded-sm"
          />
        </div>
      )}

      {/* Message Content */}
      <div
        className={cn(
          'max-w-[80%] rounded-lg px-3 py-2 text-sm',
          isUser
            ? 'bg-primary text-primary-foreground'
            : 'bg-muted text-foreground'
        )}
      >
        {isUser ? (
          <p className="whitespace-pre-wrap break-words">{contentToShow}</p>
        ) : (
          <MarkdownRenderer content={contentToShow} />
        )}

        {/* Tool Calls (only for assistant messages) */}
        {!isUser && message.toolCalls && message.toolCalls.length > 0 && (
          <div className="mt-2 pt-2 border-t border-border/50">
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={() => setShowToolCalls(!showToolCalls)}
            >
              {showToolCalls ? (
                <ChevronUp className="h-3 w-3 mr-1" />
              ) : (
                <ChevronDown className="h-3 w-3 mr-1" />
              )}
              {message.toolCalls.length} tool call{message.toolCalls.length > 1 ? 's' : ''}
            </Button>

            {showToolCalls && (
              <div className="mt-2 space-y-2">
                {message.toolCalls.map((tool, index) => (
                  <div
                    key={index}
                    className="text-xs bg-background/50 rounded p-2"
                  >
                    <div className="font-medium text-muted-foreground">
                      {tool.name}
                    </div>
                    {tool.error ? (
                      <div className="text-destructive mt-1">{tool.error}</div>
                    ) : tool.result ? (
                      <>
                        <pre className="mt-1 text-xs overflow-x-auto max-h-24 overflow-y-auto">
                          {typeof tool.result === 'string'
                            ? tool.result
                            : JSON.stringify(tool.result, null, 2)}
                        </pre>
                        {/* Action buttons for tool results */}
                        <div className="flex gap-1 mt-2">
                          {/* Show button - navigate to the relevant tab */}
                          {tool.result?.navigation && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-6 text-xs"
                              onClick={() => handleShowNavigation(tool.result!.navigation!)}
                            >
                              <ExternalLink className="h-3 w-3 mr-1" />
                              Show
                            </Button>
                          )}
                          {/* Revert button - only for create operations that succeeded and haven't been reverted */}
                          {isCreateTool(tool.name) &&
                           tool.result?.success &&
                           !tool.result?.reverted &&
                           onRevert && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 text-xs text-destructive hover:text-destructive"
                              onClick={() => handleRevert(tool, index)}
                              disabled={revertingToolIndex === index}
                            >
                              <Undo2 className="h-3 w-3 mr-1" />
                              {revertingToolIndex === index ? 'Reverting...' : 'Revert'}
                            </Button>
                          )}
                        </div>
                      </>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Pending Tools Confirmation */}
        {!isUser && message.pendingTools && message.pendingTools.length > 0 && onConfirmTools && (
          <div className="mt-3 p-2 bg-amber-500/10 border border-amber-500/30 rounded-md">
            <div className="text-xs font-medium text-amber-600 dark:text-amber-400 mb-2">
              Actions requiring confirmation:
            </div>
            <ul className="text-xs space-y-1 mb-2">
              {message.pendingTools.map((tool, index) => (
                <li key={index} className="flex items-start gap-1">
                  <span className="text-amber-500">•</span>
                  <span>{tool.description}</span>
                </li>
              ))}
            </ul>
            <Button
              size="sm"
              className="h-7 text-xs bg-green-600 hover:bg-green-700"
              onClick={handleConfirmTools}
              disabled={isConfirming}
            >
              {isConfirming ? (
                <>
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  Executing...
                </>
              ) : (
                <>
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Confirm & Execute
                </>
              )}
            </Button>
          </div>
        )}

        {/* Structured Questions Form */}
        {!isUser && message.questions && message.questions.length > 0 && !questionsSubmitted && (
          <div className="mt-3 pt-2 border-t border-border/50">
            <AIQuestionsForm
              questions={message.questions}
              businessId={businessId ?? null}
              onSubmit={handleQuestionsSubmit}
            />
          </div>
        )}

        {/* Timestamp */}
        <div
          className={cn(
            'text-[10px] mt-1 opacity-70',
            isUser ? 'text-right' : 'text-left'
          )}
        >
          {new Date(message.timestamp).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>
      </div>
    </div>
  );
};

export default AIMessage;
